import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { toast } from 'vue-sonner'
import { api, endpoints } from '../services/api.js'
import { FORMATEUR_STATUT_BADGE_CONFIG } from '../constants/formateur.js'
import { useSortableTable } from './ui/useSortableTable.js'
import { useDebouncedListSearch } from './ui/useDebouncedListSearch.js'
import { useListFilterHandlers } from './ui/useListFilterHandlers.js'
import { usePageSizePref } from './useListPreferences.js'
import { useProgressiveList } from './useProgressiveList.js'
import { RAYON_DEFAUT_KM } from '../constants/formateurCiblage.js'

/**
 * Ce que le ciblage envoie au serveur — et RIEN quand il n'est pas posé.
 *
 * Exporté parce que la CARTE pose exactement la même question au serveur, sur
 * une autre route : les deux vues doivent traduire l'état des filtres de la
 * même façon, sous peine de montrer deux mondes pour un seul écran.
 *
 * La fourchette de prix ne part que resserrée : envoyer ses bornes complètes
 * ferait écarter, côté serveur, les formateurs sans tarif — alors qu'on n'a
 * rien demandé.
 */
/**
 * Les neuf filtres ordinaires de la page, tels qu'ils partent au serveur.
 *
 * Ils étaient énumérés en chaîne de `if` côté liste et bouclés sur un tableau
 * de clés côté carte : deux listes du même jeu, qu'un filtre ajouté d'un côté
 * aurait fait diverger en silence — la carte et la liste ne regardant alors
 * plus tout à fait le même monde. C'est exactement ce que `parametresCiblage`
 * évitait déjà pour l'autre moitié.
 */
const CLES_FILTRES = ['search', 'statut', 'type', 'reseau', 'dejaTravaille', 'vue', 'motif', 'categorie', 'departement']

export function parametresFiltres(filtres) {
  const params = {}
  for (const cle of CLES_FILTRES) {
    if (filtres[cle]) params[cle] = filtres[cle]
  }
  // Les compétences exigées ensemble voyagent en JSON, comme le matériel : le
  // libellé affiché dans les pastilles reste côté écran. Dès qu'il y en a une,
  // la catégorie ne filtre plus : elle ne sert qu'à choisir où piocher la
  // suivante, et changer de domaine pour ajouter « Secourisme › PSC » ne doit
  // pas écarter en silence ceux qui n'ont encore rien en secourisme.
  if (filtres.competences?.length) {
    delete params.categorie
    params.competences = JSON.stringify(filtres.competences.map(({ categorie, sousCategorie }) => ({ categorie, sousCategorie })))
  }
  return { ...params, ...parametresCiblage(filtres) }
}

export function parametresCiblage(filtres) {
  const params = {}
  if (filtres.point) {
    params.lat = filtres.point.lat
    params.lon = filtres.point.lon
    if (filtres.rayonKm) params.rayonKm = filtres.rayonKm
  }
  if (filtres.tarif) {
    params.tarifMin = filtres.tarif[0]
    params.tarifMax = filtres.tarif[1]
  }
  if (filtres.materiel?.length) params.materiel = JSON.stringify(filtres.materiel)
  return params
}

export function useFormateursList() {
  const router = useRouter()
  const formateurs = ref([])
  const total = ref(0)
  const page = ref(1)
  const { pageSize: limit, changePageSize } = usePageSizePref('formateurs', () => loadFormateurs(1))
  const loading = ref(false)
  /** Bornes du curseur de prix pour le périmètre courant, `[bas, haut]` ou null. */
  const tarifBornes = ref(null)
  /** Combien la fourchette de prix écarte faute de tarif renseigné. */
  const sansTarif = ref(0)
  // `vue` dit QUELLE MOITIÉ de la base on regarde ('' = les fiches en
  // circulation, 'poubelle' = celles qui en ont été écartées), `motif` filtre à
  // l'intérieur de la corbeille. Les deux voyagent avec les autres filtres pour
  // que la mémoire de liste (C47) ramène l'écran d'où l'on vient — revenir
  // d'une fiche supprimée sur la liste ordinaire serait perdre sa place.
  //
  // L'état neuf est factorisé dans `filtresVides()` : repris tel quel à
  // l'initialisation et à `resetFilters()`, il ne doit exister qu'à un seul
  // endroit sous peine qu'un filtre ajouté à l'un des deux manque à l'autre.
  //
  // LE CIBLAGE (`point`, `rayonKm`, `tarif`, `materiel`) EST UN FILTRE COMME
  // LES AUTRES, et c'est délibéré : il est né sur la carte, mais autour d'où,
  // à quel prix et avec quel matériel sont des questions qu'on se pose aussi en
  // lisant la liste. Le poser ici, plutôt que dans le composable de la carte,
  // lui donne d'un coup tout ce que les autres filtres ont déjà — le retour à
  // la page 1, la réinitialisation, la mémoire de liste (C47), et surtout la
  // garantie que les deux vues d'un même écran regardent le MÊME monde.
  function filtresVides() {
    return {
      search: '', statut: '', type: '', reseau: '', dejaTravaille: '', vue: '', motif: '', categorie: '', departement: '',
      // Les compétences exigées ENSEMBLE : `[{ categorie, sousCategorie, libelle }]`.
      // Le formateur doit les avoir toutes — un seul pour deux formations.
      competences: [],
      // Le point autour duquel on mesure : `{ lat, lon, precision, libelle }`.
      point: null,
      // Un RÉGLAGE tant qu'aucun point n'est posé — un rayon sans centre ne
      // filtre rien —, d'où sa valeur de départ qui n'allume pas « filtres posés ».
      rayonKm: RAYON_DEFAUT_KM,
      // Fourchette de prix : `null` = toute l'étendue, donc aucun filtre.
      tarif: null,
      // Matériel exigé : `[{ id, minQuantite }]`, même forme que sur la carte
      // de diffusion d'une mission.
      materiel: [],
    }
  }

  const filters = ref(filtresVides())

  // Tri serveur : les colonnes acceptées sont celles de FORMATEUR_SORT_COLUMNS
  // (server/repositories/formateur.repository.js).
  const { sort, sortBy } = useSortableTable({
    defaultColumn: 'createdAt',
    defaultOrder: 'DESC',
    onSort: () => loadFormateurs(1),
  })

  // « Tout afficher » : la liste arrive par tranches, au fil du défilement.
  const progressive = useProgressiveList({
    pageSize: limit,
    items: formateurs,
    total,
    loadPage: (p) => loadFormateurs(p),
  })

  const hasActiveFilters = computed(() => {
    const f = filters.value
    // `vue` n'est pas un filtre mais une destination : ouvrir la corbeille
    // n'allume pas « Réinitialiser les filtres », qui rendrait la liste
    // ordinaire sans le dire.
    return !!(
      f.search || f.statut || f.type || f.reseau || f.dejaTravaille || f.motif || f.categorie || f.competences?.length || f.departement ||
      // Le ciblage : un point posé (avec son rayon), une fourchette de prix
      // resserrée, du matériel exigé. Le rayon seul n'y figure pas — sans
      // point, il ne retient personne.
      f.point || f.tarif || f.materiel?.length
    )
  })

  async function loadFormateurs(p = 1) {
    const chunk = progressive.begin(p)
    loading.value = !chunk.append
    page.value = p
    try {
      const params = {
        page: p,
        limit: chunk.limit,
        offset: chunk.offset,
        sortBy: sort.column,
        sortOrder: sort.order.toLowerCase(),
        // Les filtres eux-mêmes, dans la traduction que la CARTE emploie aussi :
        // une seule liste de clés pour les deux vues (cf. parametresFiltres).
        ...parametresFiltres(filters.value),
      }
      const data = await api.get(endpoints.formateurs.list(params))
      if (!chunk.apply(data.formateurs || [])) return
      total.value = data.total || 0
      // Les bornes du curseur de prix et ce que la fourchette écarte faute de
      // tarif connu viennent du serveur : c'est lui qui connaît la population
      // entière, la liste n'en voit qu'une page.
      tarifBornes.value = data.tarifBornes || null
      recalerFourchette()
      sansTarif.value = data.sansTarif || 0
    } catch (err) {
      if (chunk.stale) return
      console.error('Erreur chargement formateurs:', err)
    } finally {
      if (!chunk.stale) loading.value = false
    }
  }

  /**
   * LES BORNES DU CURSEUR DE PRIX BOUGENT AVEC LES AUTRES FILTRES : elles
   * décrivent les tarifs déclarés dans le périmètre courant. Poser [300, 500]
   * puis filtrer sur un département dont le moins cher est à 400 laissait la
   * poignée basse hors de sa piste — le serveur filtrait juste, l'écran
   * montrait une fourchette impossible. La fourchette se recale donc sur les
   * nouvelles bornes, et s'efface quand elle les recouvre entièrement : elle ne
   * filtre alors plus rien, et le dire autrement serait mentir.
   */
  function recalerFourchette() {
    const bornes = tarifBornes.value
    const fourchette = filters.value.tarif
    if (!fourchette) return
    if (!bornes) {
      filters.value.tarif = null
      return
    }
    const bas = Math.max(bornes[0], Math.min(fourchette[0], bornes[1]))
    const haut = Math.min(bornes[1], Math.max(fourchette[1], bornes[0]))
    filters.value.tarif = bas <= bornes[0] && haut >= bornes[1] ? null : [bas, haut]
  }

  // Recharger à chaque frappe inonderait le serveur : la recherche attend que
  // l'utilisateur ait fini de taper.
  const { handleSearchChange, cancel: cancelDebouncedSearch } = useDebouncedListSearch(
    () => filters.value.search,
    () => loadFormateurs(1),
    { track: 'formateurs' },
  )

  // Poser un filtre suppose toujours les trois mêmes gestes : écrire la valeur,
  // revenir à la première page, recharger. Le rechargement déclenché par un
  // filtre (et non par la pagination) est le seul cas qui intéresse le panneau
  // d'activité, d'où `track`.
  const { set: setFilter } = useListFilterHandlers(
    { filters, currentPage: page },
    {
      load: () => loadFormateurs(1),
      onSearch: handleSearchChange,
      track: 'formateurs',
      // Pas de cascade : les compétences retenues le restent quand on change
      // de catégorie pour en ajouter une d'un autre domaine.
    },
  )

  function resetFilters() {
    cancelDebouncedSearch()
    // On reste où l'on est : réinitialiser les filtres DANS la corbeille ne
    // doit pas en sortir.
    filters.value = { ...filtresVides(), vue: filters.value.vue }
    loadFormateurs(1)
  }

  function goTo(id) {
    router.push(`/formateurs/${id}`)
  }

  // ─── Corbeille ──────────────────────────────────────────────────────────────
  //
  // Une VUE, pas un filtre : elle montre tout ce qui a été écarté de la liste —
  // fiches supprimées, formateurs passés inactifs, blacklistés — là où elle ne
  // montrait que les supprimées. Les deux autres populations restaient dans la
  // liste ordinaire, mêlées aux fiches qu'on peut missionner, ce qui obligeait
  // à lire chaque ligne pour savoir laquelle comptait vraiment.
  //
  // C'est aussi la seule vue où la liste porte un geste, la restauration :
  // partout ailleurs elle ne fait que mener aux fiches.
  const enCorbeille = computed(() => filters.value.vue === 'poubelle')

  /** Fiche en cours de restauration — son bouton attend, les autres non. */
  const restaurationEnCours = ref(null)

  // Le tri suit la colonne qu'on regarde, et repart de la date d'inscription
  // aux DEUX bascules : aucune des trois populations de la corbeille ne partage
  // une date d'écartement (une fiche supprimée n'a pas de date de désactivation,
  // une inactive pas de date de suppression), et trier sur une colonne que deux
  // tiers des lignes n'ont pas renverrait toutes en fin de liste. Chaque motif
  // retrouve sa propre date dès qu'on le filtre — la colonne « Situation »
  // devient alors triable sur elle (cf. FormateursListGrid, SITUATION_SORT_KEY),
  // et c'est SEULEMENT dans ce cas que la barre de résultats l'annonce
  // (cf. SORT_DESCRIPTIONS, FormateursList.vue) : mélangées, aucune date
  // affichée ne correspond au critère de tri, la barre reste donc muette.
  //
  // `motif` retombe à vide aux deux bascules : il n'a de sens que DANS la
  // corbeille (hasActiveFilters l'ignore autrement).
  function basculerVue(vue) {
    Object.assign(sort, { column: 'createdAt', order: 'DESC' })
    filters.value.motif = ''
    setFilter('vue', vue)
  }

  function ouvrirCorbeille() {
    // `statut` n'existe pas dans la branche poubelle du serveur (cf.
    // clausePoubelle) : le conserver allumerait « Réinitialiser les filtres »
    // sur un écran où le sélecteur Statut a lui-même disparu de la barre.
    filters.value.statut = ''
    basculerVue('poubelle')
  }

  function fermerCorbeille() {
    basculerVue('')
  }

  /**
   * Restaurer une fiche DEPUIS la liste.
   *
   * La ligne quitte forcément la corbeille : on recharge la page plutôt que de
   * la retirer du tableau à la main, sinon le compteur et la pagination
   * parleraient d'un total que le serveur ne connaît plus. Et si c'était la
   * dernière de sa page, on recule d'une page — rester sur une page vide en
   * annonçant « 12 formateurs trouvés » est le genre de silence qu'on prend
   * pour une panne.
   */
  async function restaurerFormateur(formateur) {
    if (restaurationEnCours.value) return
    restaurationEnCours.value = formateur.id
    try {
      const restaure = await api.post(endpoints.formateurs.restore(formateur.id))
      const statut = FORMATEUR_STATUT_BADGE_CONFIG[restaure?.statut]?.label
      const missions = restaure?.missionsRestaurees || 0
      toast.success(
        `Fiche restaurée${statut ? ` — statut « ${statut} »` : ''}`
        + (missions ? ` (+${missions} mission${missions > 1 ? 's' : ''} proposée${missions > 1 ? 's' : ''} restaurée${missions > 1 ? 's' : ''})` : '')
      )
      await loadFormateurs(page.value)
      if (!formateurs.value.length && page.value > 1) await loadFormateurs(page.value - 1)
    } catch (err) {
      console.error('Erreur restauration formateur:', err)
      toast.error(err?.message || 'Erreur lors de la restauration')
    } finally {
      restaurationEnCours.value = null
    }
  }

  // La page COURANTE, pas la page 1 : quand on revient d'une fiche,
  // `useListStateMemory` a déjà reposé la page mémorisée (son `onBeforeMount`
  // passe avant tout `onMounted`), et ce chargement-ci est le seul qui part.
  // Forcer la page 1 ici périmait la page rendue (C47).
  onMounted(() => loadFormateurs(page.value))

  return {
    formateurs,
    total,
    page,
    limit,
    loading,
    filters,
    hasActiveFilters,
    tarifBornes,
    sansTarif,
    sort,
    sortBy,
    loadFormateurs,
    // Poser un filtre, quel qu'il soit : la carte de filtres v2 n'émet plus
    // qu'un évènement, `update:filter({ key, value })`.
    setFilter,
    changePageSize,
    resetFilters,
    goTo,
    progressive,
    enCorbeille,
    ouvrirCorbeille,
    fermerCorbeille,
    restaurerFormateur,
    restaurationEnCours,
  }
}
